import { Grid } from '@material-ui/core';
import { useState } from 'react';
import { Chart } from 'react-google-charts';
import {
  GoogleChartWrapper,
  GoogleChartWrapperChartType,
  GoogleDataTable,
  ReactGoogleChartEvent,
} from 'react-google-charts/dist/types';
import { AuditBatchApi } from './audit-batch-api';
import { AuditReportSummary, BubbleChartData } from './audit-report-api';
import { orange, red, yellow } from './theme';

/*

TODO: extract chart config

function pieChartOptions(opt?: any): any {
  return {
    ...{
      title: 'Visit Demographics',
      colors: [orange, green],
      backgroundColor: { fill: 'transparent' } as any,
      chartArea: { width: '92%', height: '84%' },
      pieHole: 0.5,
      legend: {
        position: 'bottom',
        layout: 'vertical',
      },
      titleTextStyle: {
        bold: true,
        color: '#777',
        fontSize: 15,
      },
    },
    ...opt,
  };
}
  */

type ChartCallback = {
  chartWrapper: GoogleChartWrapper;
};
type KeyValue<T = any> = [string, T, string?];
type KeyValueFn = (value: KeyValue) => void;

function keyValue<T = any>(
  table: GoogleDataTable,
  selectedItem: any,
  chartType: GoogleChartWrapperChartType
): KeyValue<T> {
  let row: number = selectedItem?.row || 0;
  let column: number = selectedItem?.column || 0;
  let key: string;
  let value: any;

  switch (chartType) {
    case 'PieChart':
      key = table.getValue(row, column) as any;
      value = table.getDistinctValues(1)[row];
      break;
    case 'BarChart':
      key = table.getColumnLabel(column);
      value = table.getValue(row, column);
      break;
    case 'ColumnChart':
      const data = (table as any).cache[row];
      key = data[3].Me;
      value = data[1].Me;
      break;
    default:
      key = table.getColumnLabel(column);
      value = table.getValue(row, column);
  }
  return [key, value];
}

const minBubbleSize = 60;
const maxBubbleSize = 168;

type BubbleProps = {
  color: string;
  bubble: BubbleChartData;
  onClick?: (a: BubbleChartData) => void;
};

function Bubble({ color, bubble, onClick }: BubbleProps) {
  const { category, totalPoints, totalPossiblePoints, percentage } = bubble;
  const size = Math.max(minBubbleSize, (maxBubbleSize * percentage) / 100);

  const [hover, setHover] = useState(false);

  const onBubbleClick = () => {
    if (onClick) {
      onClick(bubble);
    }
  };

  return (
    <div
      onClick={onBubbleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: size,
        width: size,
        backgroundColor: color,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        border: hover ? 'solid 4px #aaa' : undefined,
        cursor: 'pointer',
      }}
    >
      <Grid
        container
        direction="column"
        style={{ width: '100%', flexWrap: 'nowrap' }}
        alignItems="center"
        justify="center"
      >
        <Grid
          item
          style={{
            fontWeight: 'bold',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
          }}
        >
          {category}
        </Grid>
        <Grid item>{percentage} %</Grid>
        <Grid item>
          {totalPoints} / {totalPossiblePoints}
        </Grid>
      </Grid>
    </div>
  );
}

function keyValueEvent(
  fn: KeyValueFn,
  labelFields?: any[][]
): ReactGoogleChartEvent[] {
  return [
    {
      eventName: 'select',
      callback: ({ chartWrapper }: ChartCallback) => {
        const type = chartWrapper.getChartType();
        const chart = chartWrapper.getChart();
        const selection = chart.getSelection();

        const dataTable = chartWrapper.getDataTable();
        const value = keyValue(dataTable, selection[0], type);

        if (labelFields?.length) {
          labelFields.forEach((a) => {
            if (a[0] === value[0]) {
              value[0] = a[1];
            }
          });
        }

        if (fn) {
          fn(value);
        }
      },
    },
  ];
}

type ChartComponentProps<T = any> = {
  data: T;
  onSelection?: KeyValueFn;
  labelFields?: [string, string][];
};

type BarChartProps = {
  title: string;
  color: string;
  keyValue: object;
  labels: object;
  onSelection?: KeyValueFn;
};

function BarChart({
  title,
  color,
  keyValue,
  labels,
  onSelection,
}: BarChartProps) {
  const columns = [
    ['Category', 'Value', { role: 'style' }, { role: 'domain' }],
  ];
  let max = 1;
  Object.entries(keyValue).forEach(([k, v]) => {
    max = Math.max(v, max);
    columns.push([labels[k], v, color, k]);
  });

  return (
    <Grid item>
      <Chart
        width={'330px'}
        height={'220px'}
        chartType="ColumnChart"
        chartEvents={keyValueEvent(onSelection)}
        loader={<div></div>}
        data={columns}
        options={{
          title: title,
          backgroundColor: { fill: 'transparent' } as any,
          chartArea: { width: '80%', height: '30%' },
          titleTextStyle: {
            bold: false,
            color: '#888',
            fontSize: 16,
          },
          colors: [orange, yellow, red],
          hAxis: {
            baselineColor: 'none',
            ticks: [],
          },
          vAxis: {
            baselineColor: 'none',
            ticks: [0, max + 1],
          },
          bar: { groupWidth: '70%' },
          legend: { position: 'none' },
        }}
      />
    </Grid>
  );
}

type BarChartsProps = {
  summary: AuditReportSummary;
  onSelection?: KeyValueFn;
  //TOOD: extract title click
  onCatgory?: (category: string) => void;
};
function BarCharts({ summary, onSelection }: BarChartsProps) {
  const { barChartData, changeReasonsDescriptions } = summary;

  return (
    <Grid
      item
      container
      direction="row"
      spacing={2}
      style={{ flexWrap: 'nowrap', width: '90%' }}
      alignItems="center"
      justify="space-between"
    >
      {Object.entries(barChartData)
        .reverse()
        .map((a, i) => {
          const onClick = (e: KeyValue) => {
            if (e) {
              const label = changeReasonsDescriptions[e[0]];
              if (label) {
                e[2] = a[0] + ' / ' + label;
              }
              onSelection(e);
            }
          };

          return (
            <Grid item key={i}>
              <BarChart
                title={a[0]}
                keyValue={a[1]}
                labels={changeReasonsDescriptions}
                color={getColor(a[0])}
                onSelection={onClick}
              />
            </Grid>
          );
        })}
    </Grid>
  );
}

function NoSummary() {
  return (
    <Grid
      container
      direction="column"
      style={{ width: '100%', height: '84vh' }}
      justify="center"
      alignItems="center"
    >
      <Grid>No Summary Available</Grid>
    </Grid>
  );
}

function getColor(id: string) {
  id = (id || '').toLowerCase();

  if (id.indexOf('icd') > -1) {
    return yellow;
  }

  if (id.indexOf('cpt') > -1) {
    return red;
  }
  return orange;
}

type AuditScoreCardProps = {
  summary: AuditReportSummary;
  api: AuditBatchApi;
};
export function AuditScoreCard({ summary, api }: AuditScoreCardProps) {
  const onBubbleChartSelection = (data: BubbleChartData) => {
    api.actions.getDetails(summary.auditId, 'bubbleCategory', [
      data.category,
      data.percentage,
      data.category,
    ]);
  };

  //TODO: 'barCategory' group event
  const onBarChartSelection = (data: any[]) => {
    api.actions.getDetails(summary.auditId, 'changeReason', data);
  };

  const all = summary.bubbleChartData.filter((a) => a.category === 'ALL')[0];
  const bubbleChartData = summary.bubbleChartData.filter(
    (a) => a.category !== 'ALL'
  );
  const w = { width: '100%' };

  return !summary ? (
    <NoSummary />
  ) : (
    <Grid
      container
      direction="column"
      spacing={0}
      style={w}
      alignItems="center"
      justify="center"
    >
      {all && (
        <Grid
          item
          container
          direction="row"
          style={w}
          alignItems="center"
          justify="center"
        >
          <Bubble
            bubble={all}
            color={getColor('all')}
            onClick={onBubbleChartSelection}
          />
        </Grid>
      )}

      <Grid
        item
        container
        direction="row"
        spacing={2}
        style={{
          ...w,
          ...{ flexWrap: 'nowrap', width: '30%' },
        }}
        alignItems="center"
        justify="space-between"
      >
        {bubbleChartData.map((a, i) => (
          <Bubble
            key={i}
            bubble={a}
            color={getColor(a.category)}
            onClick={onBubbleChartSelection}
          />
        ))}
      </Grid>

      <Grid item>
        <BarCharts summary={summary} onSelection={onBarChartSelection} />
      </Grid>
    </Grid>
  );
}
